/**
 * Provider presentation helpers — brand mark, mark sizing, and display name.
 * Shared by the admin grid, the Add-a-connection picker, and the user's Linked
 * Accounts section so the same provider always looks and reads the same.
 */

import githubLogo from "@/assets/images/brand-logos/github.svg";
import odooLogo from "@/assets/images/brand-logos/odoo.svg";
import slackLogo from "@/assets/images/brand-logos/slack.png";
import gmailLogo from "@/assets/images/brand-logos/google-workspace.svg";
import telegramLogo from "@/assets/images/brand-logos/telegram.svg";
import whatsappLogo from "@/assets/images/brand-logos/whatsapp.svg";
import teamsLogo from "@/assets/images/brand-logos/microsoft-teams.svg";
import microsoft365Logo from "@/assets/images/brand-logos/microsoft-365.svg";

const LOGO_BY_ID: Record<string, string> = {
  github: githubLogo,
  odoo: odooLogo,
  slack: slackLogo,
  gmail: gmailLogo,
  telegram: telegramLogo,
  whatsapp: whatsappLogo,
  teams: teamsLogo,
  microsoft: microsoft365Logo,
};

/**
 * Resolves a connection's `logo` to an `<img src>`. Returns a known bundled
 * asset for catalog ids, the value itself when it's already a URL/path (custom
 * configs may store one directly), or undefined to fall back to a letter avatar.
 */
export function resolveProviderLogo(logo?: string): string | undefined {
  if (!logo) return undefined;
  if (logo.startsWith("http") || logo.startsWith("/") || logo.includes(".")) {
    return logo;
  }
  return LOGO_BY_ID[logo];
}

/**
 * Human-readable names for connector ids, for the cases where the connection's
 * own `displayName` isn't available — chiefly when we must name a provider the
 * workspace hasn't configured, so there's no catalog row to read it from.
 */
const NAME_BY_ID: Record<string, string> = {
  github: "GitHub",
  odoo: "Odoo",
  slack: "Slack",
  gmail: "Gmail",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  teams: "Microsoft Teams",
  microsoft: "Microsoft 365",
};

/**
 * Resolves a connector id to a display name. Falls back to a title-cased form
 * of the id so a custom connector still reads as a name rather than a slug.
 *
 * @param provider - Connector id (e.g. "teams", "custom:acme-crm")
 */
export function providerDisplayName(provider: string): string {
  const id = provider.startsWith("custom:") ? provider.slice("custom:".length) : provider;
  const known = NAME_BY_ID[id];
  if (known) return known;
  return id
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Returns the display size override for brand marks that read too small at the
 * default icon size. Shared by admin setup and member linked-account surfaces.
 */
export function providerLogoSizeClass(logo?: string): string {
  switch (logo) {
    case "odoo":
    case "slack":
    case "teams":
    case "microsoft":
    case "whatsapp":
    case "telegram":
      return "h-7 w-7";
    default:
      return "";
  }
}
